import { t } from '../../i18n';
import type {
  AgingBucket,
  CompanyExpenseAgingReport,
  CompanyExpenseByVendorReport,
  CompanyExpensePaymentsReport,
  CompanyExpenseTypeAnalysisReport,
  CompanyExpenseYearlyMonthlyReport,
} from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import type { ExcelCell } from '../../utils/exportExcelXml';
import { escapeHtml } from '../../utils/printDocument';
import { dateCell, moneyCell } from '../customers/exportCustomerShared';
import {
  downloadReportExcel,
  printReportPdf,
  type ReportMetaLine,
} from './exportReportShared';
import type { ExportFormat } from './exportReports';

const AGING_BUCKETS: AgingBucket[] = [
  'notDue',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

function str(value: string): ExcelCell {
  return { type: 'string', value };
}

function dash(value: string | null | undefined): string {
  return value && value.trim() ? value : '—';
}

export function exportCompanyExpenseYearlyMonthly(args: {
  report: CompanyExpenseYearlyMonthlyReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.companyExpenseYearlyMonthly;
  const meta: ReportMetaLine[] = [
    { label: labels.year, value: String(args.report.year) },
  ];
  const headers = [
    labels.month,
    labels.lineCount,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
    t().financial.invoice,
    t().financial.cash,
  ];
  const dataRows: ExcelCell[][] = args.report.months.map((m) => [
    str(t().common.months[m.month - 1]),
    str(String(m.count)),
    moneyCell(m.net),
    moneyCell(m.vat),
    moneyCell(m.gross),
    moneyCell(m.invoice),
    moneyCell(m.cash),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: `company-expense-yearly-${args.report.year}.xls`,
      title,
      meta,
      columnCount: 7,
      columnWidths: [14, 10, 12, 12, 12, 12, 12],
      sections: [{ headers, isNumeric: (i) => i >= 1, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i >= 1,
        rowHtml: args.report.months.map(
          (m) =>
            `<tr><td>${escapeHtml(t().common.months[m.month - 1])}</td><td class="num">${m.count}</td><td class="num">${escapeHtml(formatMoney(m.net))}</td><td class="num">${escapeHtml(formatMoney(m.vat))}</td><td class="num">${escapeHtml(formatMoney(m.gross))}</td><td class="num">${escapeHtml(formatMoney(m.invoice))}</td><td class="num">${escapeHtml(formatMoney(m.cash))}</td></tr>`,
        ),
        totalsRowHtml: `<tr><td>${escapeHtml(t().common.total)}</td><td class="num">${args.report.totals.count}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.net))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.gross))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.invoice))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.cash))}</td></tr>`,
      },
    ],
  });
}

export function exportCompanyExpenseByVendor(args: {
  report: CompanyExpenseByVendorReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.companyExpenseByVendor;
  const meta: ReportMetaLine[] = [];
  if (args.report.dateFrom)
    meta.push({ label: labels.dateFrom, value: args.report.dateFrom });
  if (args.report.dateTo)
    meta.push({ label: labels.dateTo, value: args.report.dateTo });
  const headers = [
    labels.companyExpenseVendor,
    labels.lineCount,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
  ];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.vendorName),
    str(String(r.count)),
    moneyCell(r.net),
    moneyCell(r.vat),
    moneyCell(r.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'company-expense-by-vendor.xls',
      title,
      meta,
      columnCount: 5,
      columnWidths: [28, 10, 12, 12, 12],
      sections: [{ headers, isNumeric: (i) => i >= 1, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i >= 1,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.vendorName)}</td><td class="num">${r.count}</td><td class="num">${escapeHtml(formatMoney(r.net))}</td><td class="num">${escapeHtml(formatMoney(r.vat))}</td><td class="num">${escapeHtml(formatMoney(r.gross))}</td></tr>`,
        ),
        totalsRowHtml: `<tr><td>${escapeHtml(t().common.total)}</td><td class="num">${args.report.totals.count}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.net))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.gross))}</td></tr>`,
      },
    ],
  });
}

export function exportCompanyExpenseTypeAnalysis(args: {
  report: CompanyExpenseTypeAnalysisReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.companyExpenseTypeAnalysis;
  const meta: ReportMetaLine[] = [];
  if (args.report.dateFrom)
    meta.push({ label: labels.dateFrom, value: args.report.dateFrom });
  if (args.report.dateTo)
    meta.push({ label: labels.dateTo, value: args.report.dateTo });
  const headers = [
    t().common.total,
    labels.lineCount,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
  ];
  const typeRows = [
    {
      label: t().financial.invoice,
      ...args.report.byType.INVOICE,
    },
    { label: t().financial.cash, ...args.report.byType.CASH },
  ];
  const settlementRows = [
    {
      label: labels.companyExpensePaid,
      ...args.report.bySettlement.paid,
    },
    {
      label: labels.companyExpenseRemaining,
      ...args.report.bySettlement.remaining,
    },
  ];
  const toCells = (
    rows: Array<{ label: string; count: number; net: string; vat: string; gross: string }>,
  ): ExcelCell[][] =>
    rows.map((r) => [
      str(r.label),
      str(String(r.count)),
      moneyCell(r.net),
      moneyCell(r.vat),
      moneyCell(r.gross),
    ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'company-expense-type-analysis.xls',
      title,
      meta,
      columnCount: 5,
      columnWidths: [20, 10, 12, 12, 12],
      sections: [
        {
          title: labels.companyExpenseTypeTitle,
          headers,
          isNumeric: (i) => i >= 1,
          dataRows: toCells(typeRows),
        },
        {
          title: labels.companyExpenseSettlementTitle,
          headers,
          isNumeric: (i) => i >= 1,
          dataRows: toCells(settlementRows),
        },
      ],
    });
    return;
  }
  const rowHtml = (
    rows: Array<{ label: string; count: number; net: string; vat: string; gross: string }>,
  ) =>
    rows.map(
      (r) =>
        `<tr><td>${escapeHtml(r.label)}</td><td class="num">${r.count}</td><td class="num">${escapeHtml(formatMoney(r.net))}</td><td class="num">${escapeHtml(formatMoney(r.vat))}</td><td class="num">${escapeHtml(formatMoney(r.gross))}</td></tr>`,
    );
  printReportPdf({
    title,
    meta,
    sections: [
      {
        title: labels.companyExpenseTypeTitle,
        headers,
        isNumeric: (i) => i >= 1,
        rowHtml: rowHtml(typeRows),
      },
      {
        title: labels.companyExpenseSettlementTitle,
        headers,
        isNumeric: (i) => i >= 1,
        rowHtml: rowHtml(settlementRows),
      },
    ],
  });
}

export function exportCompanyExpensePayments(args: {
  report: CompanyExpensePaymentsReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.companyExpensePayments;
  const meta: ReportMetaLine[] = [];
  if (args.report.dateFrom)
    meta.push({ label: labels.dateFrom, value: args.report.dateFrom });
  if (args.report.dateTo)
    meta.push({ label: labels.dateTo, value: args.report.dateTo });
  const headers = [
    labels.companyExpensePaymentDate,
    labels.companyExpenseVendor,
    labels.companyExpenseDocumentNo,
    t().categories.name,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
  ];
  const dataRows: ExcelCell[][] = args.report.lines.map((r) => [
    dateCell(r.paymentDate),
    str(dash(r.vendorName)),
    str(dash(r.documentNo)),
    str(r.categoryName),
    moneyCell(r.netAmount),
    moneyCell(r.vatAmount),
    moneyCell(r.grossAmount),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'company-expense-payments.xls',
      title,
      meta,
      columnCount: 7,
      columnWidths: [12, 22, 14, 18, 12, 12, 12],
      sections: [{ headers, isNumeric: (i) => i >= 4, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i >= 4,
        rowHtml: args.report.lines.map(
          (r) =>
            `<tr><td>${escapeHtml(formatDate(r.paymentDate))}</td><td>${escapeHtml(dash(r.vendorName))}</td><td>${escapeHtml(dash(r.documentNo))}</td><td>${escapeHtml(r.categoryName)}</td><td class="num">${escapeHtml(formatMoney(r.netAmount))}</td><td class="num">${escapeHtml(formatMoney(r.vatAmount))}</td><td class="num">${escapeHtml(formatMoney(r.grossAmount))}</td></tr>`,
        ),
        totalsRowHtml: `<tr><td colspan="4">${escapeHtml(t().common.total)}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.net))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.gross))}</td></tr>`,
      },
    ],
  });
}

export function exportCompanyExpenseAging(args: {
  report: CompanyExpenseAgingReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.companyExpenseAging;
  const meta: ReportMetaLine[] = [
    { label: labels.asOf, value: formatDate(args.report.asOf) },
  ];
  const headers = [
    labels.bucket,
    labels.lineCount,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
  ];
  const dataRows: ExcelCell[][] = AGING_BUCKETS.map((bucket) => {
    const b = args.report.buckets[bucket];
    return [
      str(labels.buckets[bucket]),
      str(String(b.count)),
      moneyCell(b.net),
      moneyCell(b.vat),
      moneyCell(b.gross),
    ];
  });
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'company-expense-aging.xls',
      title,
      meta,
      columnCount: 5,
      columnWidths: [18, 10, 12, 12, 12],
      sections: [{ headers, isNumeric: (i) => i >= 1, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i >= 1,
        rowHtml: AGING_BUCKETS.map((bucket) => {
          const b = args.report.buckets[bucket];
          return `<tr><td>${escapeHtml(labels.buckets[bucket])}</td><td class="num">${b.count}</td><td class="num">${escapeHtml(formatMoney(b.net))}</td><td class="num">${escapeHtml(formatMoney(b.vat))}</td><td class="num">${escapeHtml(formatMoney(b.gross))}</td></tr>`;
        }),
        totalsRowHtml: `<tr><td>${escapeHtml(t().common.total)}</td><td class="num">${args.report.totals.count}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.net))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(args.report.totals.gross))}</td></tr>`,
      },
    ],
  });
}
